import { useMutation } from '@tanstack/react-query';
import { Alert, Button, Form, Input, Select, Space } from 'antd';
import { AxiosError } from 'axios';
import { useContext, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { BsPhone } from 'react-icons/bs';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from '../../config/axios.ts';
import { AuthContext } from '../../context/auth.context.tsx';
import AuthLayout from '../../layouts/auth.layout.tsx';
import { CalculatorOutlined, QrcodeOutlined } from '@ant-design/icons';

type FieldType = {
	phone: string;
	password: string;
	remember: string;
	countryCode: string;
};

type OtpFieldType = {
	otp: string;
};

const Login = () => {
	const [step, setStep] = useState<'login' | 'verify'>('login');
	const { setUser, setIsAuthenticated, setRole } = useContext(AuthContext);
	const [alertMessage, setAlertMessage] = useState<string | null>(null);
	const [hashedOtp, setHashedOtp] = useState<string | null>(null);
	const location = useLocation();
	const navigate = useNavigate();
	const [loginCredentials, setLoginCredentials] = useState<FieldType | null>(null);
	const [countryCode, setCountryCode] = useState<string>('256');

	// send verification code mutation
	const { mutate: sendVerificationCode, isPending: sendVerificationCodePending } = useMutation({
		mutationFn: async (values: FieldType) => {
			console.log(values);
			const phoneWithCode = `${countryCode}${values.phone}`;
			setLoginCredentials({ ...values, countryCode });
			const { data } = await axios.post('/otp/send', {
				phone_number: phoneWithCode,
			});
			return data;
		},
		onSuccess: (data) => {
			toast.success('Verification code sent');
			setStep('verify');
			setHashedOtp(data.results.hashed_otp);
		},
		onError: (error: AxiosError<{ msg: string }>) => {
			if (error.response) {
				setAlertMessage(error.response.data.msg);
			}
		},
	});

	// login with phone mutation
	const { mutate: loginWithPhone, isPending: loginWithPhonePending } = useMutation({
		mutationFn: async (values: OtpFieldType) => {
			if (!loginCredentials) {
				setAlertMessage('Please enter your phone number and password');
				return;
			}

			const phoneWithCode = loginCredentials?.countryCode + loginCredentials?.phone;
			const { data } = await axios.post('/admin/users/login', {
				hashed_otp: hashedOtp,
				user_given_otp: values.otp,
				phone_number: phoneWithCode,
				password: loginCredentials?.password,
			});
			return data;
		},
		onSuccess: (data) => {
			window.localStorage.setItem('device_id', data.results.access_token);
			toast.success('Login successful');
			setUser(data.results.user);
			setRole(data.results.user.role);
			setIsAuthenticated(true);
			if (data.results.user.role === 'retailer') {
				window.location.href = '/retailer/dashboard';
			} else {
				window.location.href = '/admin/dashboard';
			}
		},
		onError: (error: AxiosError<{ msg: string }>) => {
			if (error.response) {
				setAlertMessage('Invalid OTP or Credentials!');
				setStep('login');
			}
		},
	});

	// hide the error message after 3 seconds
	useEffect(() => {
		const timer = setTimeout(() => {
			setAlertMessage(null);
		}, 3000);
		return () => clearTimeout(timer);
	}, [alertMessage]);

	// get message from navigate state
	const state_message = location.state && location.state.message;

	const selectBefore = (
		<Select
			defaultValue={'+256'}
			prefix={<BsPhone />}
			onChange={(value) => setCountryCode(value)}
		>
			<Select.Option value="256">+256</Select.Option>
			<Select.Option value="88">+88</Select.Option>
		</Select>
	);

	return (
		<AuthLayout>
			<div className="flex h-full items-center justify-center">
				<div className="flex w-full max-w-[450px] flex-col items-center justify-center">
					<Link to={'/'}>
						<img
							src="/images/logo.png"
							alt="logo"
							className="h-14"
						/>
					</Link>

					<div className="pt-4" />

					{/* Quick Action Buttons */}
					<Space
						size="large"
						className="mb-6 flex w-full justify-center"
					>
						<Button
							size="large"
							icon={<CalculatorOutlined />}
							onClick={() => navigate('/calculator')}
							className="flex h-16 w-36 items-center justify-center text-lg shadow-md"
						>
							Calculator
						</Button>
						<Button
							size="large"
							icon={<QrcodeOutlined />}
							onClick={() => navigate('/qr-code')}
							className="flex h-16 w-36 items-center justify-center text-lg shadow-md"
						>
							QR Code
						</Button>
					</Space>

					<h1 className="text-center text-4xl font-bold text-black-7 dark:text-white">Welcome Back</h1>

					<div className="pt-4" />

					<p className="text-base font-medium text-gray-400 dark:text-gray-200">Just pop your phone number and password in here </p>

					<div className="pt-6" />

					{alertMessage ? (
						<Alert
							message={alertMessage}
							type={alertMessage.includes('successful') ? 'success' : 'error'}
							showIcon
							className="w-full"
						/>
					) : state_message ? (
						<Alert
							message={state_message}
							type={state_message.includes('successful') ? 'success' : 'error'}
							showIcon
							className="w-full"
						/>
					) : null}

					{step === 'login' ? (
						<Form
							initialValues={{ remember: true }}
							onFinish={(values) => {
								sendVerificationCode(values);
							}}
							layout="vertical"
							className="w-full pt-2"
							requiredMark={false}
						>
							<Form.Item<FieldType>
								label={<div className="pl-1 text-base font-medium text-black-7 dark:text-white">Phone Number</div>}
								name="phone"
							>
								<Input
									addonBefore={selectBefore}
									size="large"
									placeholder="Enter your phone number"
									required
									type="tel"
									className="h-11"
									onKeyDown={(e) => {
										// If the key is not a number (0-9), prevent input
										if (!/^\d$/.test(e.key) && !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(e.key)) {
											e.preventDefault();
										}
									}}
									onChange={(e) => {
										// Remove any non-numeric characters
										e.target.value = e.target.value.replace(/\D/g, '');
									}}
								/>
							</Form.Item>

							<Form.Item<FieldType>
								label={<div className="pl-1 text-base font-medium text-black-7 dark:text-white">Password</div>}
								name="password"
							>
								<Input.Password
									size="large"
									placeholder="Enter your password"
									required
									type="password"
									className="h-11"
								/>
							</Form.Item>

							<div className="flex items-center justify-between pb-3.5">
								<div className="mb-2"></div>
								<Link to={'/forgot-password'}>
									<span className="text-base font-medium text-[#6E5DED]">Forgot password?</span>
								</Link>
							</div>

							<Form.Item className="mb-2">
								<Button
									htmlType="submit"
									block
									size="large"
									shape="round"
									className="h-11 font-bold"
									type="primary"
									loading={sendVerificationCodePending}
									disabled={sendVerificationCodePending}
								>
									Send Verification Code
								</Button>
							</Form.Item>
						</Form>
					) : (
						<Form
							onFinish={(values) => {
								loginWithPhone(values);
							}}
							layout="vertical"
							className="w-full pt-2"
							requiredMark={false}
						>
							<div className="flex items-center justify-center">
								<Form.Item<OtpFieldType>
									label={''}
									name="otp"
								>
									<Input.OTP
										length={4}
										size="large"
										className="h-11"
										separator={<span> - </span>}
									/>
								</Form.Item>
							</div>

							<Form.Item className="mb-2">
								<Button
									htmlType="submit"
									block
									size="large"
									shape="round"
									className="h-11 font-bold"
									type="primary"
									loading={loginWithPhonePending}
									disabled={loginWithPhonePending}
								>
									Verify
								</Button>
							</Form.Item>
						</Form>
					)}

					<div className="pt-3" />
				</div>
			</div>
		</AuthLayout>
	);
};

export default Login;
